/**
 * Client-side game world + fixed-timestep loop.
 *
 * Owns the shared World and drives it at the fixed 20 Hz tick, render-
 * interpolating EVERY entity (player, bot, projectiles) between the last two
 * ticks. Movement/combat are never reimplemented here — we set the player's
 * intent and call the shared `step()`. A match is (re)created via `start()`.
 */
import {
  createWorld,
  spawnEntity,
  step,
  withTransform,
  withKind,
  vec2,
  length,
  FIXED_DT,
  CHARACTERS,
  ARENAS,
  DEFAULT_CHARACTER,
  DEFAULT_BOT_CHARACTER,
  DEFAULT_ARENA,
  type CharacterId,
  type ArenaId,
  type EntityId,
  type EntityKind,
  type Vec2,
  type World,
} from "@allstars/shared";
import type { InputSample } from "../input/types.js";

const MAX_STEPS_PER_FRAME = 5;

export interface EntityView {
  id: EntityId;
  kind: EntityKind;
}

export interface PickupView {
  pos: Vec2;
  active: boolean;
}

export type Outcome = "playing" | "won" | "lost";

export interface LocalGame {
  readonly playerId: EntityId;
  /** Start a fresh match with the chosen player character + arena. */
  start(playerChar?: CharacterId, arena?: ArenaId): void;
  /** Restart the same match (same character/arena, new RNG seed). */
  reset(): void;
  advance(dt: number, input: InputSample): number;
  entities(): EntityView[];
  interpolatedTransform(id: EntityId, alpha: number): Vec2;
  aimOf(id: EntityId): Vec2;
  healthOf(id: EntityId): { current: number; max: number } | null;
  pickups(): PickupView[];
  arenaId(): ArenaId;
  playerSpeed(): number;
  outcome(): Outcome;
}

interface Snap {
  prev: Vec2;
  cur: Vec2;
}

export const createLocalGame = (): LocalGame => {
  let seed = 1;
  let lastChar: CharacterId = DEFAULT_CHARACTER;
  let lastArena: ArenaId = DEFAULT_ARENA;
  let world: World = createWorld(seed, lastArena);
  let playerId: EntityId = 0;
  let acc = 0;
  let queuedFire = false;
  let queuedAim: Vec2 = vec2(0, 0);
  const snapshots = new Map<EntityId, Snap>();

  const seedSnapshots = (): void => {
    snapshots.clear();
    for (const id of withTransform(world)) {
      const t = world.transform[id];
      if (t) snapshots.set(id, { prev: { ...t.pos }, cur: { ...t.pos } });
    }
  };

  const start = (playerChar: CharacterId = DEFAULT_CHARACTER, arena: ArenaId = DEFAULT_ARENA): void => {
    lastChar = playerChar;
    lastArena = arena;
    world = createWorld(seed, arena);
    const spawns = ARENAS[arena].spawns;

    const player = spawnEntity(world, "player");
    world.transform[player] = { pos: { ...spawns.player }, vel: vec2(0, 0) };
    world.health[player] = { current: CHARACTERS[playerChar].maxHealth, max: CHARACTERS[playerChar].maxHealth };
    world.intent[player] = { move: vec2(0, 0), aim: vec2(0, 1), fire: false };
    world.weapon[player] = { cooldown: 0 };
    world.character[player] = playerChar;
    playerId = player;

    const bot = spawnEntity(world, "bot");
    world.transform[bot] = { pos: { ...spawns.bot }, vel: vec2(0, 0) };
    world.health[bot] = { current: CHARACTERS[DEFAULT_BOT_CHARACTER].maxHealth, max: CHARACTERS[DEFAULT_BOT_CHARACTER].maxHealth };
    world.intent[bot] = { move: vec2(0, 0), aim: vec2(0, -1), fire: false };
    world.weapon[bot] = { cooldown: 0 };
    world.character[bot] = DEFAULT_BOT_CHARACTER;

    acc = 0;
    queuedFire = false;
    queuedAim = vec2(0, 0);
    seedSnapshots();
  };
  start();

  const reset = (): void => {
    seed += 1;
    start(lastChar, lastArena);
  };

  const syncSnapshots = (): void => {
    const seen = new Set<EntityId>();
    for (const id of withTransform(world)) {
      const t = world.transform[id];
      if (!t) continue;
      seen.add(id);
      const snap = snapshots.get(id);
      if (snap) {
        snap.prev = snap.cur;
        snap.cur = { x: t.pos.x, y: t.pos.y };
      } else {
        snapshots.set(id, { prev: { ...t.pos }, cur: { ...t.pos } });
      }
    }
    for (const id of snapshots.keys()) if (!seen.has(id)) snapshots.delete(id);
  };

  const advance = (dt: number, input: InputSample): number => {
    acc += dt;
    if (input.fire) {
      queuedFire = true;
      queuedAim = input.aim;
    }
    let steps = 0;
    while (acc >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      const intent = world.intent[playerId];
      if (intent) {
        intent.move = input.move;
        if (queuedFire) {
          intent.fire = true;
          intent.aim = queuedAim;
          queuedFire = false;
        } else {
          intent.fire = false;
          intent.aim = input.aim;
        }
      }
      step(world);
      syncSnapshots();
      acc -= FIXED_DT;
      steps += 1;
    }
    if (acc > FIXED_DT) acc = FIXED_DT;
    return acc / FIXED_DT;
  };

  const interpolatedTransform = (id: EntityId, alpha: number): Vec2 => {
    const snap = snapshots.get(id);
    if (!snap) {
      const t = world.transform[id];
      return t ? { x: t.pos.x, y: t.pos.y } : vec2(0, 0);
    }
    return {
      x: snap.prev.x + (snap.cur.x - snap.prev.x) * alpha,
      y: snap.prev.y + (snap.cur.y - snap.prev.y) * alpha,
    };
  };

  const entities = (): EntityView[] =>
    withTransform(world).map((id) => ({ id, kind: world.kind[id] ?? "projectile" }));

  const aimOf = (id: EntityId): Vec2 => world.intent[id]?.aim ?? vec2(0, 0);

  const healthOf = (id: EntityId): { current: number; max: number } | null =>
    world.health[id] ?? null;

  const pickups = (): PickupView[] =>
    world.pickups.map((s) => ({ pos: s.pos, active: s.cooldown === 0 }));

  const playerSpeed = (): number => length(world.transform[playerId]?.vel ?? vec2(0, 0));

  const alive = (id: EntityId): boolean => (world.health[id]?.current ?? 0) > 0;

  const outcome = (): Outcome => {
    if (!alive(playerId)) return "lost";
    return withKind(world, "bot").some(alive) ? "playing" : "won";
  };

  return {
    get playerId(): EntityId {
      return playerId;
    },
    start,
    reset,
    advance,
    entities,
    interpolatedTransform,
    aimOf,
    healthOf,
    pickups,
    arenaId: (): ArenaId => world.arenaId,
    playerSpeed,
    outcome,
  };
};
